/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {AwesomeIcon} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import {IconName} from '@fortawesome/fontawesome-svg-core';
import checkIcon from '@iconify/icons-bi/check';
import exclamationCircle from '@iconify/icons-bi/exclamation-circle';
import crossCircled from '@iconify/icons-radix-icons/cross-circled';
import {Icon} from '@iconify/react';
import classNames from 'classnames';
import React, {MouseEventHandler} from 'react';
import Account from '../models/accountModel';
import Invoice from '../models/invoiceModel';
import User from '../models/userModel';
import {hasAdminLogin} from './adminUtil';
import {InvoiceOptionName, PeppolState} from './enums';
import {AccessKey, AccountStatus, InvoiceRequestStatus, InvoiceStatus, InvoiceType, OrderStatus} from './interfaces';
import {InvoiceOption, InvoiceOptionConfig} from './types';

export type ApiIcon = {
    labelClassName?: string;
    icon: React.ReactElement;
    iconLabel: string;
    onClick?: MouseEventHandler;
};
export type InvoiceStatusElement = {type?: string; element: React.ReactElement<any, any>};

const availableInvoiceOptions: Record<InvoiceOptionName, InvoiceOptionConfig> = {
    [InvoiceOptionName.SET_NUMBER]: {
        label: 'Factuurnummer instellen...',
        type: InvoiceOptionName.SET_NUMBER,
        icon: <AwesomeIcon name='file-invoice' className='icon' />,
        accessKey: AccessKey.INVOICE_INVOICE_NUMBER,
        validator: (invoice: Invoice) => invoice.orderStatus === OrderStatus.SENT && !invoice.invoiceNum
    },
    [InvoiceOptionName.PREVIEW]: {
        label: 'Voorbeeld',
        type: InvoiceOptionName.PREVIEW,
        icon: <AwesomeIcon name='eye' className='icon' />,
        accessKey: AccessKey.INVOICE_DOWNLOAD
    },
    [InvoiceOptionName.EDIT]: {
        label: 'Wijzigen...',
        type: InvoiceOptionName.EDIT,
        icon: <AwesomeIcon name='pen' className='icon' />,
        accessKey: [AccessKey.INVOICE_EDIT_CUSTOMER, AccessKey.INVOICE_EDIT_ITEMS]
    },
    [InvoiceOptionName.DOWNLOAD]: {
        label: 'Downloaden',
        type: InvoiceOptionName.DOWNLOAD,
        icon: <AwesomeIcon name='download' className='icon' />,
        accessKey: AccessKey.INVOICE_DOWNLOAD
    },
    [InvoiceOptionName.DETAILS]: {
        label: 'Details',
        type: InvoiceOptionName.DETAILS,
        icon: <AwesomeIcon name='list' className='icon' />,
        accessKey: AccessKey.INVOICE_DETAILS
    },
    [InvoiceOptionName.SEND_INVOICE]: {
        label: 'Factuur verzenden...',
        type: InvoiceOptionName.SEND_INVOICE,
        icon: <AwesomeIcon name='paper-plane' className='icon' />,
        accessKey: AccessKey.INVOICE_SEND
    },
    [InvoiceOptionName.UPLOAD_INVOICE]: {
        label: 'Factuur uploaden',
        type: InvoiceOptionName.UPLOAD_INVOICE,
        icon: <AwesomeIcon name='cloud-arrow-up' className='icon' />,
        accessKey: AccessKey.INVOICE_REQUEST_UPLOAD,
        validator: (invoice: Invoice) =>
            invoice.orderStatus === OrderStatus.SENT &&
            !!invoice.invoiceNum &&
            invoice.uploadStatus !== InvoiceRequestStatus.INVOICE_UPLOADED
    },
    [InvoiceOptionName.CREDIT]: {
        label: (invoice: Invoice) => (invoice.hasCreditInvoice ? 'Creditfactuur details' : 'Crediteren...'),
        type: InvoiceOptionName.CREDIT,
        icon: <AwesomeIcon name='euro-sign' className='icon' />,
        accessKey: AccessKey.INVOICE_CREDIT,
        validator: (invoice: Invoice) =>
            invoice.orderStatus === OrderStatus.SENT &&
            !!invoice.invoiceNum &&
            invoice.invoiceType !== InvoiceType.CREDIT
    },
    [InvoiceOptionName.REVERSE_CHARGE]: {
        label: 'BTW verleggen...',
        type: InvoiceOptionName.REVERSE_CHARGE,
        icon: <AwesomeIcon name='receipt' prefix='fas' className='icon' />,
        accessKey: AccessKey.INVOICE_REVERSE_CHARGE,
        validator: (invoice: Invoice) =>
            invoice.orderStatus === OrderStatus.SENT &&
            !!invoice.invoiceNum &&
            !invoice.hasReversedCharge &&
            !invoice.getFlex('reversed_invoice_id') &&
            invoice.invoiceType === InvoiceType.DEBET
    },
    [InvoiceOptionName.CANCEL_EMAILS]: {
        label: 'Mails annuleren',
        type: InvoiceOptionName.CANCEL_EMAILS,
        icon: <AwesomeIcon name='times-circle' className='icon' />,
        accessKey: AccessKey.INVOICE_CANCEL_EMAILS,
        validator: (invoice: Invoice) => !invoice.getFlex('prevent_lambda') && !!invoice.willSendAt.length
    },
    [InvoiceOptionName.SEND_PEPPOL]: {
        label: 'Verzenden via Peppol',
        type: InvoiceOptionName.SEND_PEPPOL,
        icon: <AwesomeIcon name='paper-plane' className='icon' />,
        accessKey: AccessKey.INVOICE_SEND,
        validator: (invoice: Invoice) =>
            invoice.peppolApplies() && (!invoice.peppolState || invoice.peppolState === PeppolState.FAILED)
    },
    [InvoiceOptionName.COPY_ID]: {
        label: 'ID kopiëren',
        type: InvoiceOptionName.COPY_ID,
        icon: <AwesomeIcon name='copy' className='icon' />,
        accessKey: AccessKey.PAGE_INVOICES,
        validator: (_: Invoice, user: User) => user.isSupport() || hasAdminLogin()
    }
};

export function getInvoiceOptions(invoice: Invoice, user: User | null): Array<InvoiceOption> {
    return Object.values(availableInvoiceOptions)
        .filter((option: InvoiceOptionConfig) => !option.validator || option.validator(invoice, user))
        .map((option: InvoiceOptionConfig) => ({
            ...option,
            label: typeof option.label === 'string' ? option.label : option.label(invoice)
        }));
}

function getInvoiceStatusColor(invoice: Invoice): string | null {
    if (invoice.uploadStatus === InvoiceRequestStatus.INVOICE_UPLOADED) return 'green';
    switch (invoice.status) {
        case InvoiceStatus.ERROR:
            return 'red';
        case InvoiceStatus.WARNING:
            return 'yellow';
        case InvoiceStatus.SENT:
            return 'green';
        case InvoiceStatus.TRANSPORT:
            return 'green-2';
        default:
            return null;
    }
}

export function getInvoiceStatusLabels(invoice: Invoice): InvoiceStatusElement[] {
    return [getInvoiceFullStatusLabel(invoice), ..._getInvoiceStatusLabels(invoice)];
}

function getInvoiceFullStatusLabel(invoice: Invoice): InvoiceStatusElement {
    if (invoice.invoiceType === InvoiceType.CREDIT)
        return {
            element: <label className='status red'>Creditfactuur</label>
        };
    const {status, translatedStatus, error} = invoice;
    if (status === InvoiceStatus.ERROR)
        return {
            element: (
                <label className='status red'>
                    {translatedStatus}
                    {error.length > 0 && <span className='invoice-error'>{error}</span>}
                </label>
            )
        };
    return {
        element: <label className={classNames('status', getInvoiceStatusColor(invoice))}>{translatedStatus}</label>
    };
}

function _getInvoiceStatusLabels(invoice: Invoice): InvoiceStatusElement[] {
    if (invoice.invoiceType === InvoiceType.CREDIT) return getCreditStatusLabels(invoice);
    const {
            orderStatus,
            shouldSend,
            downloaded,
            edited,
            sentReview,
            hasReversedCharge,
            hasCreditInvoice,
            uploadStatus,
            isSent
        } = invoice,
        elements: InvoiceStatusElement[] = [];
    const isUploaded = uploadStatus === InvoiceRequestStatus.INVOICE_UPLOADED;

    if (orderStatus === OrderStatus.PLACED)
        elements.push({element: renderStatusCircle('should-send', 'Bestelling nog niet verzonden', 'box')});
    else if (orderStatus === OrderStatus.SENT)
        elements.push({element: renderStatusCircle('order-sent', 'Bestelling verzonden', 'truck')});
    else if (orderStatus === OrderStatus.CANCELED)
        elements.push({element: renderStatusCircle('error', 'Bestelling geannuleerd', 'times')});
    if (isSent) {
        if (isUploaded) elements.push({element: renderStatusCircle('blue', 'Factuur verzonden', 'paper-plane')});
    } else {
        if (shouldSend) elements.push({element: renderStatusCircle('should-send', 'Wordt verzonden', 'paper-plane')});
        else {
            const sendElement = getWillSendAt(invoice);
            if (sendElement) elements.push(sendElement);
        }
    }
    if (downloaded) elements.push({element: renderStatusCircle('downloaded', 'Gedownload', 'download')});
    if (edited) elements.push({element: renderStatusCircle('edited', 'Gewijzigd', 'pen')});
    if (sentReview) elements.push({element: renderStatusCircle('sent-review', 'Reviewverzoek verzonden', 'star')});
    if (hasReversedCharge) elements.push({element: renderStatusCircle('downloaded', 'BTW verlegd', 'ICP', true)});
    else if (hasCreditInvoice) elements.push({element: renderStatusCircle('credit', 'Gecrediteerd', 'C', true)});
    if (invoice.orderStatus === OrderStatus.SENT && !invoice.invoiceNum)
        elements.push({
            type: 'invoice-num',
            element: renderStatusCircle('should-send', 'Zonder factuurnummer', 'file-invoice')
        });
    return elements;
}

function getWillSendAt(invoice: Invoice): InvoiceStatusElement | null {
    const {willSendAtMoment} = invoice;
    const today = IDate.now().startOf('day');
    if (!willSendAtMoment || today > willSendAtMoment || invoice.getFlex('prevent_lambda') === true) return null;
    if (today.isAt(willSendAtMoment, 'day'))
        return {
            element: renderStatusCircle('should-send', 'E-mail wordt vandaag verzonden', 'paper-plane')
        };
    const days = today.absDiff(willSendAtMoment.startOf('day'), 'days');
    const daysLabel = `${days} ${days === 1 ? 'dag' : 'dagen'}`;
    return {element: renderStatusCircle('should-send', `E-mail wordt over ${daysLabel} verzonden`, 'paper-plane')};
}

function getCreditStatusLabels(invoice: Invoice): InvoiceStatusElement[] {
    const {sent, downloaded, edited, shouldSend} = invoice,
        elements: InvoiceStatusElement[] = [];
    if (sent) elements.push({element: renderStatusCircle('downloaded', 'Verzonden', 'paper-plane')});
    if (!sent && shouldSend)
        elements.push({element: renderStatusCircle('should-send', 'Wordt verzonden', 'paper-plane')});
    if (downloaded) elements.push({element: renderStatusCircle('downloaded', 'Gedownload', 'download')});
    if (edited) elements.push({element: renderStatusCircle('edited', 'Gewijzigd', 'pen')});
    return elements;
}

function renderStatusCircle(
    name: string,
    label: string,
    icon: IconName | string,
    iconIsText: boolean = false
): React.ReactElement {
    const iconElement = iconIsText ? (
        <span className='status-label text'>{icon}</span>
    ) : (
        <AwesomeIcon name={icon as IconName} className='status-label' />
    );
    return (
        <div className={classNames('status-circle', name)}>
            {iconElement}
            <label className='status-full-label'>{label}</label>
        </div>
    );
}

export function getApiIcons(account: Account): ApiIcon[] {
    switch (account.status) {
        case AccountStatus.SETUP_API:
            return [
                {
                    labelClassName: 'yellow',
                    icon: <Icon icon={crossCircled} className='label-icon' />,
                    iconLabel: 'Geen verbinding met Bol'
                }
            ];
        case AccountStatus.ERROR:
            return [
                {
                    labelClassName: 'red',
                    icon: <Icon icon={exclamationCircle} className='label-icon' />,
                    iconLabel: account.error
                }
            ];
        case AccountStatus.IMPORTING:
            return [
                {
                    labelClassName: 'green',
                    icon: <Icon icon={checkIcon} className='label-icon single-check' />,
                    iconLabel: 'Gekoppeld met Bol'
                }
            ];
        case AccountStatus.IDLE:
            return [
                {
                    labelClassName: 'green',
                    icon: <Icon icon={checkIcon} className='label-icon single-check' />,
                    iconLabel: 'Gekoppeld met Bol'
                }
            ];
    }
}
